import { ChangeEvent, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CustomButton } from '@medtronic/pecc-react-component-library-js';
import { Stack, Typography } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { format } from 'date-fns';
import { ETLJobHistory } from '~/api/types';
import { useGetETLJobsHistoryQuery } from '~/api/v2/hooks';
import { SearchTextField, Table, Tooltip } from '~/components';
import { PAGE_SIZE } from '~/components/Table';
import { useDebounce } from '~/hooks';
import { truncateStyles } from '~/pages/StoragePage/components/styles';
import { DATE_FORMAT, TIME_FORMAT, getDurationTime } from '~/utils';
import { JobStatusCell, ViewLogsButtonCell } from '.';
import { actionButtonStyles } from './styles';

const JobHistoryTab = () => {
  const projectId = +useParams().projectid!;
  const [paginationModel, setPaginationModel] = useState({
    pageSize: PAGE_SIZE,
    page: 0,
  });
  const [searchText, setSearchText] = useState<string>('');

  const {
    data: jobHistory,
    isFetching,
    refetch,
  } = useGetETLJobsHistoryQuery({
    projectId,
    page: paginationModel.page + 1,
    size: paginationModel.pageSize,
    search: useDebounce(searchText),
  });

  const columns: GridColDef<ETLJobHistory>[] = [
    {
      field: 'schedule_name',
      headerName: 'Job name',
      renderCell: (params) => (
        <Tooltip title={params.row.schedule_name}>
          <Typography sx={truncateStyles}>{params.row.schedule_name || '—'}</Typography>
        </Tooltip>
      ),
    },
    {
      field: 'job_run_id',
      sortable: false,
      headerName: 'Run id',
    },
    {
      field: 'state',
      headerName: 'Status',
      sortable: false,
      renderCell: (params) => <JobStatusCell status={params?.value} details={params.row.state_details} />,
    },
    {
      field: 'execution_date',
      sortable: false,
      headerName: 'Execution date',
      renderCell: ({ row }: GridRenderCellParams) =>
        row.execution_date ? format(new Date(`${row.execution_date}Z`), `${DATE_FORMAT} ${TIME_FORMAT}`) : '—',
    },
    {
      field: 'execution_time_seconds',
      sortable: false,
      headerName: 'Run time',
      flex: 0.5,
      renderCell: ({ row }) => getDurationTime(row.execution_time_seconds || 0),
    },
    {
      field: 'attempt',
      sortable: false,
      headerName: 'Attempts',
      flex: 0.5,
      renderCell: ({ row }) => row.attempt ?? '—',
    },
    {
      field: '_',
      sortable: false,
      headerName: 'Logs',
      renderCell: ({ row }) => <ViewLogsButtonCell jobId={row.job_run_id} jobName={row.schedule_name} />,
      headerAlign: 'center',
      align: 'center',
      flex: 0.6,
    },
  ];

  return (
    <Stack spacing={2}>
      <Typography variant="h5">Job History</Typography>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <SearchTextField
          sx={{ width: 250 }}
          size="small"
          onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchText(e.target.value)}
          value={searchText}
          placeholder="Search"
          onClear={() => setSearchText('')}
        />
        <CustomButton
          buttonType="outlined"
          disabled={isFetching}
          sx={actionButtonStyles}
          onClick={() => refetch()}
          label={isFetching ? 'Updating' : 'Refresh'}
        />
      </Stack>
      <Table
        getRowId={(row) => row.job_run_id}
        rows={jobHistory?.data || []}
        columns={columns}
        loading={isFetching}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[PAGE_SIZE]}
        rowCount={jobHistory?.pagination.total || 0}
        paginationMode="server"
      />
    </Stack>
  );
};

export default JobHistoryTab;
